import React from 'react';
import { Card, CardHeader, CardContent } from './ui/card';

// Import all the existing components
import RatesSummary from "./RatesSummary";
import ICESchedules from "./ICESchedules";
import AccountManagement from "./AccountManagement";
import VarianceMonitoring from "./VarianceMonitoring";
import AuditTrail from "./AuditTrail";
import AdvancedAllocations from "./AdvancedAllocations";
import ContractAllocations from "./ContractAllocations";
import GovernanceDashboard from "./GovernanceDashboard";
import AIAssistant from "./AIAssistant";
import ReconciliationDashboard from "./ReconciliationDashboard";

const MainContent = ({ 
  activeTab, 
  calculation, 
  trialBalanceAccounts, 
  setTrialBalanceAccounts,
  adjustments,
  setAdjustments
}) => {

  const renderContent = () => {
    switch (activeTab) {
      case 'upload':
        return (
          <Card>
            <CardHeader>
              <h3 className="text-xl font-bold">Upload Trial Balance</h3>
              <p className="text-gray-600">
                Start by uploading your trial balance Excel file to begin the DCAA compliance process.
              </p>
            </CardHeader>
            <CardContent>
              <div className="text-center py-12">
                <div className="text-gray-400 mb-4">
                  <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                  </svg>
                </div>
                <p className="text-gray-600">
                  Use the upload section above to get started, or select an existing calculation from the list.
                </p>
              </div>
            </CardContent>
          </Card>
        );

      case 'rates':
        return calculation ? (
          <RatesSummary 
            calculation={calculation}
            trialBalanceAccounts={trialBalanceAccounts}
          />
        ) : (
          <div className="text-center py-12">
            <p className="text-gray-600">Please upload a trial balance file to view rates summary.</p>
          </div>
        );

      case 'schedules':
        return calculation ? (
          <ICESchedules 
            calculationId={calculation.id}
            filename={calculation.filename}
            calculation={calculation}
            trialBalanceAccounts={trialBalanceAccounts}
          />
        ) : (
          <div className="text-center py-12">
            <p className="text-gray-600">Please upload a trial balance file to view ICE schedules.</p>
          </div>
        );

      case 'adjustments':
        return calculation ? (
          <AccountManagement 
            calculation={calculation}
            trialBalanceAccounts={trialBalanceAccounts}
            setTrialBalanceAccounts={setTrialBalanceAccounts}
            adjustments={adjustments}
            setAdjustments={setAdjustments}
          />
        ) : (
          <div className="text-center py-12">
            <p className="text-gray-600">Please upload a trial balance file to make adjustments.</p>
          </div>
        );

      case 'variance':
        return calculation ? (
          <VarianceMonitoring 
            calculationId={calculation.id}
            filename={calculation.filename}
          />
        ) : (
          <div className="text-center py-12">
            <p className="text-gray-600">Please upload a trial balance file to monitor variances.</p>
          </div>
        );

      case 'allocations':
        return calculation ? (
          <AdvancedAllocations 
            calculationId={calculation.id}
            trialBalanceAccounts={trialBalanceAccounts}
          />
        ) : (
          <div className="text-center py-12">
            <p className="text-gray-600">Please upload a trial balance file to configure allocations.</p>
          </div>
        );

      case 'contracts':
        return calculation ? (
          <ContractAllocations />
        ) : (
          <div className="text-center py-12">
            <p className="text-gray-600">Please upload a trial balance file to manage contract allocations.</p>
          </div>
        );

      case 'ai-assistant':
        return calculation ? (
          <AIAssistant 
            calculationId={calculation.id}
            accounts={trialBalanceAccounts}
            onAccountUpdate={(update) => {
              setTrialBalanceAccounts(prev => 
                prev.map(account => 
                  account.id === update.account_id 
                    ? { ...account, pool: update.pool }
                    : account
                )
              );
            }}
          />
        ) : (
          <div className="space-y-6">
            <Card className="border-blue-200 bg-gradient-to-r from-blue-50 to-blue-50">
              <CardHeader>
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-blue-100 rounded-lg">
                    <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-blue-800">AI Assistant Ready</h3>
                    <p className="text-blue-600">
                      Upload a trial balance to unlock AI-powered mapping assistance
                    </p>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="text-center p-4 bg-white/60 rounded-lg">
                    <div className="text-2xl font-bold text-blue-600">GPT-4o</div>
                    <div className="text-sm text-blue-800">Natural Language</div>
                  </div>
                  <div className="text-center p-4 bg-white/60 rounded-lg">
                    <div className="text-2xl font-bold text-blue-600">Auto-Map</div>
                    <div className="text-sm text-blue-800">Smart Suggestions</div>
                  </div>
                  <div className="text-center p-4 bg-white/60 rounded-lg">
                    <div className="text-2xl font-bold text-green-600">Learning</div>
                    <div className="text-sm text-green-800">Pattern Recognition</div>
                  </div>
                  <div className="text-center p-4 bg-white/60 rounded-lg">
                    <div className="text-2xl font-bold text-indigo-600">Chat</div>
                    <div className="text-sm text-indigo-800">DCAA Guidance</div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        );

      case 'governance':
        return calculation ? (
          <GovernanceDashboard 
            calculationId={calculation.id}
            isActive={activeTab === 'governance'}
          />
        ) : (
          <div className="text-center py-12">
            <p className="text-gray-600">Please upload a trial balance file to access governance features.</p>
          </div>
        );

      case 'audit':
        return calculation ? (
          <AuditTrail 
            calculationId={calculation.id}
            filename={calculation.filename}
          />
        ) : (
          <div className="text-center py-12">
            <p className="text-gray-600">Please upload a trial balance file to view audit trail.</p>
          </div>
        );

      case 'reconciliation':
        return <ReconciliationDashboard />;

      default:
        return (
          <div className="text-center py-12">
            <p className="text-gray-600">Select a section from the sidebar to get started.</p>
          </div>
        );
    }
  };

  return (
    <div className="flex-1 bg-gray-50 overflow-auto">
      <div className="p-6">
        {renderContent()}
      </div>
    </div>
  );
};

export default MainContent;